import dayjs from "dayjs";

export interface FeatureAnnouncementData {
  id: string;
  dateShown: string;
  dateReminded: string | null;
}

export enum FeatureAnnouncementKeys {
  FEATURE_ANNOUNCEMENTS = "FEATURE_ANNOUNCEMENTS",
}

const featureAnnouncementService = () => {
  const getStoredFeatureAnnouncements = (): FeatureAnnouncementData[] => {
    try {
      const json = localStorage.getItem(
        FeatureAnnouncementKeys.FEATURE_ANNOUNCEMENTS,
      );
      if (!json) return [];

      const parsed = JSON.parse(json);

      if (!Array.isArray(parsed)) {
        return [];
      }

      return parsed;
    } catch (error) {
      localStorage.removeItem(FeatureAnnouncementKeys.FEATURE_ANNOUNCEMENTS);
      return [];
    }
  };

  const saveFeatureAnnouncements = (
    featureAnnouncements: FeatureAnnouncementData[],
  ) => {
    localStorage.setItem(
      FeatureAnnouncementKeys.FEATURE_ANNOUNCEMENTS,
      JSON.stringify(featureAnnouncements),
    );
  };

  const recordInitialView = (featureAnnouncementId: string) => {
    const featureAnnouncements = getStoredFeatureAnnouncements();
    if (featureAnnouncements.find((c) => c.id === featureAnnouncementId))
      return;

    featureAnnouncements.push({
      id: featureAnnouncementId,
      dateShown: dayjs().toISOString(),
      dateReminded: null,
    });
    saveFeatureAnnouncements(featureAnnouncements);
  };

  const postponeFeatureAnnouncement = (featureAnnouncementId: string) => {
    const featureAnnouncements = getStoredFeatureAnnouncements();
    const index = featureAnnouncements.findIndex(
      (c) => c.id === featureAnnouncementId,
    );

    if (index === -1) return;

    const featureAnnouncement = featureAnnouncements[index];

    if (featureAnnouncement.dateReminded) {
      featureAnnouncements.splice(index, 1);
    } else {
      featureAnnouncements[index] = {
        ...featureAnnouncement,
        dateReminded: dayjs().add(1, "day").toISOString(),
      };
    }

    saveFeatureAnnouncements(featureAnnouncements);
  };

  const dismissFeatureAnnouncement = (featureAnnouncementId: string) => {
    const featureAnnouncements = getStoredFeatureAnnouncements();
    const filtered = featureAnnouncements.filter(
      (c) => c.id !== featureAnnouncementId,
    );
    saveFeatureAnnouncements(filtered);
  };

  const shouldShowReminder = (featureAnnouncementId: string): boolean => {
    const featureAnnouncements = getStoredFeatureAnnouncements();
    const featureAnnouncement = featureAnnouncements.find(
      (c) => c.id === featureAnnouncementId,
    );

    if (!featureAnnouncement || !featureAnnouncement.dateReminded) {
      return false;
    }

    return dayjs().isAfter(dayjs(featureAnnouncement.dateReminded));
  };

  const clearFeatureAnnouncements = () => {
    localStorage.removeItem(FeatureAnnouncementKeys.FEATURE_ANNOUNCEMENTS);
  };

  return {
    recordInitialView,
    postponeFeatureAnnouncement,
    dismissFeatureAnnouncement,
    shouldShowReminder,
    clearFeatureAnnouncements,
  };
};

export default featureAnnouncementService;
